import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'

import { media } from 'utilities/styled'
import { withApolloV4Provider } from 'utilities/apollo'
import useGpsrDialog from 'hooks/use-gpsr-dialog'

import Button from 'components/atoms/button'
import Checkbox from 'components/atoms/checkbox'
import ExplanationParagraph from 'components/atoms/explanation-paragraph'
import LegendBoxContainer from 'components/atoms/legend-box-container'
import LoadingIndicator from 'components/atoms/loading-indicator'
import Text from 'components/atoms/text'
import Portal from 'components/molecules/portal'
import FlexibleDialog from 'components/molecules/flexible-dialog'
import GpsrDialogForm from 'components/molecules/gpsr-dialog-form'

const Container = styled.div`
  margin-bottom: 40px;
  min-height: 75vh;
`

const PortalsContainer = styled.div`
  margin-bottom: 40px;
`

const PortalSection = styled.div`
  margin-bottom: 40px;
`

const Title = styled(Text)`
  margin-bottom: 16px;
  text-transform: capitalize;
`

const StyledLegendBoxContainer = styled(LegendBoxContainer)`
  flex-direction: column;
  margin-bottom: ${(props) => props.theme.sizings.lvl3};

  ${media.tablet`
    width: 50%;
  `}
`

const StyledExplanationParagraph = styled(ExplanationParagraph)`
  margin: 0;
`

const PortalsForm = ({
  onSubmit,
  portals,
  published,
  disabled,
  disabledMessage,
  gpsrUserRequest,
  carFile,
  carId,
}) => {
  const { t } = useTranslation()
  const [selectedPortals, setSelectedPortals] = useState()

  const [dontShowGPSRMessageAgain, setDontShowGPSRMessageAgain] = useState(
    carFile?.gpsrMessageOff || false,
  )

  const {
    isGpsrDialogOpen,
    formikRef,
    handleCloseGpsrDialog,
    handleGpsrFormSubmit,
  } = useGpsrDialog(carFile, carId)

  const handleFormSubmitWithCheckbox = () => {
    if (formikRef.current) {
      const formValues = formikRef.current.values
      const valuesWithCheckbox = {
        ...formValues,
        dontShowGPSRMessageAgain,
      }
      handleGpsrFormSubmit(valuesWithCheckbox)
    }
  }
  const recommendedPortals = portals.filter(
    (portal) => portal.type === 'recommended',
  )
  const businessPortals = portals.filter((portal) => portal.type === 'business')
  const otherPortals = portals.filter((portal) => portal.type === 'other')
  const defaultSelectedPortals = portals.reduce(
    (accumulator, { id, selected }) => {
      if (!selected) {
        return accumulator
      }
      return [...accumulator, id]
    },
    [],
  )

  const portalSections = [recommendedPortals, businessPortals, otherPortals]
    .filter(
      (section) => section && Array.isArray(section) && section.length > 0,
    )
    .map((section) => ({
      title: t(section[0].type),
      portals: section,
    }))
  const submitText = published ? t('saveChanges') : t('publishOnInternet')

  useEffect(() => {
    if (
      defaultSelectedPortals &&
      defaultSelectedPortals.length &&
      !selectedPortals
    ) {
      setSelectedPortals(defaultSelectedPortals)
    }
  }, [defaultSelectedPortals, selectedPortals, setSelectedPortals])

  const handleChange = (selected, id) => {
    if (selected) {
      setSelectedPortals([...(selectedPortals || []), id])
    } else {
      setSelectedPortals([...selectedPortals.filter((portal) => portal !== id)])
    }
  }

  const handleSubmit = () => {
    onSubmit({
      portals_selected: selectedPortals,
    })
  }

  return (
    <Container>
      {!portals || portals.loading ? (
        <LoadingIndicator />
      ) : (
        <>
          {disabled && !!disabledMessage && (
            <StyledLegendBoxContainer
              legendBackgroundColor="alternativeBackground"
              legendColor="chartsIsLvl2Bad"
            >
              <StyledExplanationParagraph>
                {disabledMessage}
              </StyledExplanationParagraph>
            </StyledLegendBoxContainer>
          )}
          <PortalsContainer>
            {portalSections.length &&
              portalSections.map((section, index) => (
                <PortalSection key={index.toString()}>
                  <Title type="h2" text={section.title} />
                  <div>
                    {section.portals.map((portal, idx) => {
                      const date = portal.updated
                        ? portal.updated.replace(/\s.*/, '')
                        : t('unknown')
                      return (
                        <Portal
                          key={idx.toString()}
                          disabled={disabled}
                          date={date}
                          log={portal.statusLog}
                          selected={
                            selectedPortals &&
                            selectedPortals.indexOf(portal.id) !== -1
                          }
                          onChange={(selected) =>
                            handleChange(selected, portal.id)
                          }
                          status={portal.status}
                          title={portal.omschrijving}
                          gpsrUserRequest={gpsrUserRequest}
                          brand={carFile.merk}
                          svidMerk={carFile.svidMerk}
                          gpsrCategoryId={carFile.gpsrCategoryId}
                          carId={carId}
                        />
                      )
                    })}
                  </div>
                </PortalSection>
              ))}
          </PortalsContainer>
          <Button
            disabled={disabled}
            level="cta"
            onClick={handleSubmit}
            text={submitText}
            type="submit"
            data-test-e2e="button-submit-portals"
          />
        </>
      )}

      <FlexibleDialog
        open={isGpsrDialogOpen}
        closeHandler={handleCloseGpsrDialog}
        title={t('gpsrForm.addGpsrDetails')}
        content={
          <GpsrDialogForm ref={formikRef} onSubmit={handleGpsrFormSubmit} />
        }
        cancel
        cancelText={t('cancel')}
        onCancel={handleCloseGpsrDialog}
        submitText={t('save')}
        onSubmit={handleFormSubmitWithCheckbox}
        scrollableContent
        additionalFooterContent={
          <Checkbox
            name="dontShowGPSRMessageAgain"
            onChange={setDontShowGPSRMessageAgain}
            title={t('gpsrForm.dontShowGPSRMessageAgain')}
            checked={dontShowGPSRMessageAgain}
          />
        }
      />
    </Container>
  )
}

PortalsForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  portals: PropTypes.array,
  published: PropTypes.number.isRequired,
  disabled: PropTypes.bool.isRequired,
  disabledMessage: PropTypes.string,
  gpsrUserRequest: PropTypes.object,
  carFile: PropTypes.object,
  carId: PropTypes.string,
}

PortalsForm.defaultProps = {
  portals: null,
  disabled: false,
  disabledMessage: null,
  carFile: null,
  carId: null,
}

export default withApolloV4Provider(PortalsForm)
